import { useEffect, useState } from "react";
import { api, apiErr, fmtINR, API_BASE } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Send, Download, Loader2 } from "lucide-react";

const PURPOSES = [
  { v: "BOOKING", l: "Vehicle Booking" },
  { v: "TOKEN", l: "Token Money" },
  { v: "EMI", l: "EMI / Instalment" },
  { v: "FULL_PAYMENT", l: "Full Payment" },
  { v: "SERVICE", l: "Service / Spares" },
  { v: "OTHER", l: "Other" },
];

const MODES = ["CASH", "UPI", "NEFT", "RTGS", "CHEQUE"];

export default function NewReceipt() {
  const nav = useNavigate();
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({
    branch_code: "",
    customer_name: "",
    mobile: "",
    pan: "",
    address: "",
    vehicle_model: "",
    chassis_no: "",
    purpose: "BOOKING",
    remarks: "",
  });
  const [payments, setPayments] = useState([{ mode: "CASH", amount: "", reference_no: "", bank_name: "" }]);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    api.get("/meta/branches").then((r) => {
      setBranches(r.data);
      setForm((f) => ({ ...f, branch_code: r.data[0]?.code || "" }));
    });
  }, []);

  const total = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);

  function updatePayment(i, k, v) {
    setPayments((ps) => ps.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  }

  async function submit(e) {
    e.preventDefault();
    if (payments.length === 0 || total <= 0) return toast.error("Add at least one payment with amount > 0");
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    if (!form.mobile.trim()) return toast.error("Mobile is required");
    setBusy(true);
    try {
      const body = {
        ...form,
        payments: payments.map((p) => ({
          mode: p.mode,
          amount: parseFloat(p.amount),
          reference_no: p.reference_no || "",
          bank_name: p.bank_name || "",
        })),
      };
      const { data } = await api.post("/receipts", body);
      setCreated(data);
      toast.success(`Receipt ${data.receipt_no} generated`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  }

  function shareWhatsApp(rc) {
    const pdfUrl = `${API_BASE}/receipts/${rc.id}/pdf`;
    const msg = `Dear ${rc.customer_name},%0A%0AThank you for your payment of ₹ ${rc.total_amount.toLocaleString("en-IN")} to Ratan Motors (Tata Motors CV).%0A%0AReceipt No: ${rc.receipt_no}%0ABranch: ${rc.branch_name}%0A%0ADownload receipt: ${pdfUrl}%0A%0A- Ratan Motors, Head Office`;
    const phone = rc.mobile.replace(/\D/g, "");
    const number = phone.length === 10 ? "91" + phone : phone;
    window.open(`https://wa.me/${number}?text=${msg}`, "_blank");
  }

  if (created) {
    return (
      <div className="max-w-3xl mx-auto space-y-6" data-testid="receipt-success">
        <div className="bg-white rounded-2xl border border-emerald-200 p-8 shadow-sm">
          <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
            Receipt Generated
          </div>
          <h1 className="text-3xl font-extrabold mt-3 mono text-slate-900">{created.receipt_no}</h1>
          <div className="text-slate-500 mt-1">For {created.customer_name} · {created.branch_name}</div>
          <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
            <Kv k="Total Amount" v={fmtINR(created.total_amount)} />
            <Kv k="Payment Count" v={created.payments.length} />
            <Kv k="Purpose" v={created.purpose} />
            <Kv k="Mobile" v={created.mobile} />
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={`${API_BASE}/receipts/${created.id}/pdf`}
              target="_blank" rel="noreferrer"
              data-testid="download-receipt-pdf"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm"
            >
              <Download className="h-4 w-4" /> View / Download PDF
            </a>
            <button
              data-testid="share-whatsapp"
              onClick={() => shareWhatsApp(created)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm"
            >
              <Send className="h-4 w-4" /> Send via WhatsApp
            </button>
            <button
              onClick={() => { setCreated(null); setPayments([{ mode: "CASH", amount: "", reference_no: "", bank_name: "" }]); setForm((f) => ({ ...f, customer_name: "", mobile: "", pan: "", address: "", vehicle_model: "", chassis_no: "", remarks: "" })); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 font-semibold text-sm text-slate-700"
            >
              <Plus className="h-4 w-4" /> New Receipt
            </button>
            <button
              onClick={() => nav("/receipts")}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 font-semibold text-sm text-slate-700"
            >
              Go to Register
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold text-slate-900">New Money Receipt</h1>
        <p className="text-slate-500 mt-1">Enter the branch that received the payment and customer details.</p>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="new-receipt-form">
        <Section title="Branch & Purpose">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Branch (receiving cash)" required>
              <select
                data-testid="branch-select"
                value={form.branch_code}
                onChange={(e) => setForm({ ...form, branch_code: e.target.value })}
                className={inputCls}
              >
                {branches.map((b) => <option key={b.code} value={b.code}>{b.name} ({b.code})</option>)}
              </select>
            </Field>
            <Field label="Purpose">
              <select
                data-testid="purpose-select"
                value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                className={inputCls}
              >
                {PURPOSES.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
            </Field>
          </div>
        </Section>

        <Section title="Customer Details">
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Customer Name" required>
              <input data-testid="cust-name" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={inputCls} placeholder="Ram Kumar" />
            </Field>
            <Field label="Mobile" required>
              <input data-testid="cust-mobile" required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className={inputCls} placeholder="9876543210" />
            </Field>
            <Field label="PAN">
              <input data-testid="cust-pan" value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} className={inputCls} placeholder="ABCDE1234F" />
            </Field>
            <Field label="Address">
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} placeholder="Village / Town" />
            </Field>
            <Field label="Vehicle Model">
              <input data-testid="veh-model" value={form.vehicle_model} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} className={inputCls} placeholder="Tata Ace Gold" />
            </Field>
            <Field label="Chassis No">
              <input value={form.chassis_no} onChange={(e) => setForm({ ...form, chassis_no: e.target.value.toUpperCase() })} className={inputCls} placeholder="MAT..." />
            </Field>
          </div>
        </Section>

        <Section title="Payment Breakdown" subtitle="Split across multiple modes if needed (e.g. ₹20k Cash + ₹30k UPI)">
          <div className="space-y-3">
            {payments.map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-start bg-slate-50 border border-slate-200 rounded-xl p-3">
                <select
                  data-testid={`payment-mode-${i}`}
                  value={p.mode}
                  onChange={(e) => updatePayment(i, "mode", e.target.value)}
                  className={inputCls + " col-span-2"}
                >
                  {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <input
                  data-testid={`payment-amount-${i}`}
                  type="number" step="0.01" min="0"
                  placeholder="Amount"
                  value={p.amount}
                  onChange={(e) => updatePayment(i, "amount", e.target.value)}
                  className={inputCls + " col-span-3"}
                />
                <input
                  data-testid={`payment-ref-${i}`}
                  placeholder={p.mode === "CASH" ? "—" : "UTR / Cheque / Txn ID"}
                  value={p.reference_no}
                  onChange={(e) => updatePayment(i, "reference_no", e.target.value)}
                  disabled={p.mode === "CASH"}
                  className={inputCls + " col-span-4 disabled:bg-slate-100"}
                />
                <input
                  placeholder="Bank (opt.)"
                  value={p.bank_name}
                  onChange={(e) => updatePayment(i, "bank_name", e.target.value)}
                  disabled={p.mode === "CASH"}
                  className={inputCls + " col-span-2 disabled:bg-slate-100"}
                />
                <button
                  type="button"
                  onClick={() => setPayments((ps) => ps.filter((_, idx) => idx !== i))}
                  disabled={payments.length === 1}
                  className="col-span-1 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg h-10 disabled:opacity-30"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              data-testid="add-payment"
              onClick={() => setPayments((ps) => [...ps, { mode: "UPI", amount: "", reference_no: "", bank_name: "" }])}
              className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700"
            >
              <Plus className="h-4 w-4" /> Add another payment
            </button>
          </div>

          <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4">
            <div className="text-sm text-slate-500">Total to be receipted</div>
            <div className="text-2xl font-extrabold mono text-slate-900" data-testid="payment-total">{fmtINR(total)}</div>
          </div>
        </Section>

        <Section title="Remarks (optional)">
          <textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className={inputCls} />
        </Section>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={busy}
            data-testid="submit-receipt"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Generate Receipt
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls = "w-full h-11 px-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition text-sm text-slate-900 bg-white";

function Section({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className="mb-4">
        <h3 className="font-bold text-slate-800">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </div>
      {children}
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </label>
  );
}

function Preview({ k, v, highlight }) {
  return (
    <div className={`rounded-lg px-2 py-1.5 ${highlight ? "bg-blue-600 text-white" : "bg-white"}`}>
      <div className={`text-[10px] uppercase tracking-wider ${highlight ? "text-blue-100" : "text-slate-500"}`}>{k}</div>
      <div className={`font-bold mono ${highlight ? "" : "text-slate-900"}`}>{v}</div>
    </div>
  );
}

function Kv({ k, v }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-400">{k}</div>
      <div className="font-semibold text-slate-800 mt-0.5">{v}</div>
    </div>
  );
}
